import { ICellRendererParams } from '@ag-grid-community/core'
import { FileOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { Button } from '@sureco/design-system'
import { Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import {
  HrisCensusReportUploadDto,
  HrisGeneratedFileDto,
} from '../../../api/dto/hris-census-report-upload.dto'
import { getPresignedGeneratedFileDownloadUrl } from '../../../api/hris-api-client'
import { useSubmitFileToCensusV1 } from '../hooks/useSubmitFileToCensusV1'
import { useSubmitFileToCensusV2 } from '../hooks/useSubmitFileToCensusV2'
import { isSubmitLocked } from '../utils/submitLock'
import CensusFilePreviewModal from './CensusFilePreviewModal'

const { Text, Link } = Typography

// The submit was handed to a background job that has not finished yet
const isSubmitInProgress = (file: HrisGeneratedFileDto): boolean =>
  file.submitStatus === 'QUEUED' || file.submitStatus === 'PROCESSING'

export const GeneratedFilesCellRenderer = (
  data: ICellRendererParams<HrisCensusReportUploadDto> & {
    onSubmit?: () => void
  }
) => {
  const { value: generatedFiles, data: hrisCensusReport } = data
  const censusVersion = hrisCensusReport?.censusVersion ?? 1
  const { submit, loadingFileId } = useSubmitFileToCensusV1()
  const { submit: submitV2, loadingFileId: loadingFileIdV2 } =
    useSubmitFileToCensusV2()
  const [previewFile, setPreviewFile] = useState<HrisGeneratedFileDto | null>(
    null
  )

  useEffect(() => {
    if (!previewFile || !Array.isArray(generatedFiles)) {
      return
    }

    const nextPreviewFile =
      generatedFiles.find((file) => file.id === previewFile.id) ?? null

    if (nextPreviewFile !== previewFile) {
      setPreviewFile(nextPreviewFile)
    }
  }, [generatedFiles, previewFile])

  if (!Array.isArray(generatedFiles) || generatedFiles.length === 0) {
    return <Text type="secondary">No files</Text>
  }

  const sortedFiles = [...generatedFiles].sort(
    (a, b) => Number(a.isError) - Number(b.isError)
  )

  const generatedRowTitle = (
    file: HrisGeneratedFileDto,
    censusVersion: number
  ) => {
    if (censusVersion === 2) {
      return `CMv2 - ${file.fileName} (${file.rowCount})`
    }
    return `${file.fileName} (${file.rowCount})`
  }

  const submittedGeneratedRowUrl = (
    file: HrisGeneratedFileDto,
    censusVersion: number
  ) => {
    if (censusVersion === 2) {
      return `/v2-census-management/${file.id}/DRAFT_EMPLOYEE`
    }

    return `/census-management/review/${file.csvImport?.publicKey}`
  }

  const submittedGeneratedRowTitle = (
    file: HrisGeneratedFileDto,
    censusVersion: number
  ) => {
    if (censusVersion === 2) {
      return `Census Management V2 - ${dayjs(file.submittedDatetime).format('MM/DD/YYYY hh:mm A')}`
    }

    return `Submitted ${dayjs(file.submittedDatetime).format('MM/DD/YYYY hh:mm A')}`
  }

  const onFileSubmit = (file: HrisGeneratedFileDto, censusVersion: number) => {
    if (censusVersion === 1 && file.isError) {
      return
    }

    if (censusVersion === 2) {
      submitV2(file.id, data?.onSubmit)
      return
    }

    submit(file.id, data?.onSubmit)
  }

  const canSubmitFile = (file: HrisGeneratedFileDto) => {
    const isLoadingHere =
      loadingFileId === file.id || loadingFileIdV2 === file.id
    return (
      !file.isSubmitted &&
      !isSubmitInProgress(file) &&
      (censusVersion === 2 || !file.isError) &&
      // Once submit() acquires the lock, isSubmitLocked(file.id) becomes true
      // on the very next render (before setLoadingFileId's update is even
      // processed) — without this override the button would unmount instead
      // of showing its loading state for the submit this instance itself
      // started. Only treat the lock as disqualifying when it's held by
      // someone else (another tab, or this tab before a reload).
      (!isSubmitLocked(file.id) || isLoadingHere)
    )
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          width: '100%',
        }}
      >
        {sortedFiles.map((file: HrisGeneratedFileDto) => (
          <div
            key={file.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              marginBottom: 2,
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                minWidth: 0,
                flex: 1,
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  minWidth: 0,
                }}
              >
                <FileOutlined />
                <Tooltip
                  color="#f9f9f9"
                  overlayInnerStyle={{ color: '#333' }}
                  title={`${generatedRowTitle(file, censusVersion)}`}
                >
                  <Link
                    className="textSm"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '100%',
                    }}
                    onClick={() => setPreviewFile(file)}
                  >
                    {generatedRowTitle(file, censusVersion)}
                  </Link>
                </Tooltip>
              </span>

              {file.isSubmitted && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <UnorderedListOutlined />
                  <Link
                    href={submittedGeneratedRowUrl(file, censusVersion)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="textGray"
                  >
                    {submittedGeneratedRowTitle(file, censusVersion)}
                  </Link>
                </span>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexShrink: 0,
                marginLeft: 'auto',
              }}
            >
              {canSubmitFile(file) ? (
                <Button
                  size="small"
                  onClick={() => onFileSubmit(file, censusVersion)}
                  loading={
                    loadingFileId === file.id || loadingFileIdV2 === file.id
                  }
                  variant="darkblue"
                >
                  Submit
                </Button>
              ) : null}
              {isSubmitInProgress(file) && !file.isSubmitted && (
                <Button size="small" variant="darkblue" loading disabled>
                  Submitting…
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <CensusFilePreviewModal
        open={previewFile !== null}
        file={previewFile}
        getDownloadUrl={getPresignedGeneratedFileDownloadUrl}
        censusVersion={censusVersion}
        onFileSubmit={onFileSubmit}
        isSubmitting={
          previewFile !== null &&
          (loadingFileId === previewFile.id ||
            loadingFileIdV2 === previewFile.id ||
            isSubmitInProgress(previewFile))
        }
        onCancel={() => setPreviewFile(null)}
      />
    </>
  )
}
